/**
 * ErrorBoundary Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Catches React errors and displays fallback UI instead of blank page.
 * Prevents entire app from crashing due to component errors.
 * 
 * Features:
 * - Error catching and logging
 * - Fallback UI display
 * - Error details in development
 * - Graceful degradation
 * - Prevents blank page syndrome
 * 
 * Dependencies:
 * - React 19.2.4 (Error boundaries)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 * - Fixed blank page issues by wrapping App component
 */
import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-rose-50 p-10 font-mono text-slate-700">
          <h1 className="text-2xl font-bold text-rose-700">Something went wrong</h1>
          <pre className="mt-4 whitespace-pre-wrap text-slate-800">
            {this.state.error?.message}
          </pre>
          <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-600">
            {this.state.error?.stack}
          </pre>
          {this.state.errorInfo && (
            <pre className="mt-4 whitespace-pre-wrap text-xs text-slate-500">
              Component Stack:{'\n'}
              {this.state.errorInfo.componentStack}
            </pre>
          )}
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              window.location.reload();
            }}
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-rose-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-rose-800"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
